/**
 * Champs de la fiche enfant — **liste fermée** (docs/02-fonctionnel.md §6).
 *
 * La fermeture de la liste *est* la mesure de minimisation. Un champ libre « notes » ou
 * « informations complémentaires » finit toujours par recevoir ce que le formulaire ne
 * demandait pas : un diagnostic, un suivi psychologique, une remarque sur l'autre parent.
 * Ces données arrivent alors sans base légale claire, sans chiffrement adapté, et sans que
 * personne ait décidé de les collecter (docs/05 §3).
 *
 * Ajouter un champ suppose donc de modifier ce fichier — et de se demander, à ce
 * moment-là, si Repère a besoin de cette donnée pour rendre son service.
 *
 * `sensible` marque ce qui relève de la santé : ces valeurs sont chiffrées au niveau du
 * champ, avec la clé du dossier. Les autres restent en clair — les chiffrer sans nécessité
 * compliquerait l'export et la relecture sans rien protéger de plus.
 */
/**
 * La clé du code postal de l'école, nommée parce qu'elle est lue ailleurs que dans la
 * fiche : l'API en déduit la zone de vacances scolaires, et le navigateur s'en sert pour
 * savoir qu'un enregistrement vient de périmer les congés affichés au calendrier. Trois
 * copies de la même chaîne finiraient par diverger d'une lettre, et le calendrier
 * cesserait silencieusement d'être rafraîchi.
 */
export const CLE_CODE_POSTAL_ECOLE = 'code_postal_ecole';
export const CHAMPS_FICHE = [
    {
        cle: 'taille_vetements',
        libelle: 'Taille de vêtements',
        sensible: false,
        aide: 'Par exemple : 8 ans, ou 128 cm.',
    },
    { cle: 'pointure', libelle: 'Pointure', sensible: false },
    {
        cle: 'allergies',
        libelle: 'Allergies',
        sensible: true,
        aide: 'Ce qui doit être connu en cas d’urgence. Chiffré au repos.',
    },
    {
        cle: 'traitements',
        libelle: 'Traitements en cours',
        sensible: true,
        aide: 'Nom, posologie, durée. Chiffré au repos.',
    },
    {
        cle: 'medecin_traitant',
        libelle: 'Médecin traitant',
        sensible: true,
        aide: 'Nom et téléphone. Chiffré au repos.',
    },
    { cle: 'groupe_sanguin', libelle: 'Groupe sanguin', sensible: true },
    { cle: 'ecole', libelle: 'École', sensible: false },
    {
        cle: CLE_CODE_POSTAL_ECOLE,
        libelle: 'Code postal de l’école',
        sensible: false,
        // Ce champ ne sert pas qu'à la fiche : c'est lui qui donne au calendrier la zone de
        // vacances scolaires. Le dire dans l'aide évite qu'il passe pour une formalité — un
        // parent qui ne le remplit pas n'aura simplement pas ses vacances au calendrier.
        aide: 'Cinq chiffres. Ils donnent la zone de vacances scolaires affichée au calendrier.',
    },
    { cle: 'enseignant', libelle: 'Enseignant', sensible: false },
    {
        cle: 'personnes_autorisees',
        libelle: 'Personnes autorisées au retrait',
        sensible: false,
        aide: 'Prénom, nom et lien avec l’enfant.',
    },
    {
        cle: 'activites',
        libelle: 'Activités et horaires',
        sensible: false,
        aide: 'Par exemple : judo le mercredi 14h–15h30, gymnase municipal.',
    },
];
const PAR_CLE = new Map(CHAMPS_FICHE.map((champ) => [champ.cle, champ]));
export function champFiche(cle) {
    return PAR_CLE.get(cle);
}
export function estChampConnu(cle) {
    return PAR_CLE.has(cle);
}
/** Vrai si la valeur de ce champ doit être chiffrée au repos. */
export function champSensible(cle) {
    return PAR_CLE.get(cle)?.sensible ?? false;
}
/** Longueur maximale d'une valeur. Assez pour une posologie, trop peu pour un récit. */
export const LONGUEUR_MAX_VALEUR = 500;
//# sourceMappingURL=fields.js.map